import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Satellite } from "lucide-react";
import { toast } from "sonner";

const RUNNING = ["PENDIENTE", "PROCESANDO"];
const PROGRESS_LABEL = {
  PENDIENTE: "En cola...",
  ANALIZANDO_FICHA: "Leyendo ficha RFFM...",
  BUSCANDO_TEMPORADAS: "Buscando temporadas...",
  ANALIZANDO_PARTIDOS: "Analizando partidos...",
  ANALIZANDO_ACTAS: "Leyendo actas...",
  CALCULANDO_ESTADISTICAS: "Calculando estadísticas...",
  COMPLETADO: "Completado",
  ERROR: "Error",
};

const fmtSync = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
};

export default function ScoutingRffmPanel({ player, isAdmin, onUpdated }) {
  const [code, setCode] = useState(player.rffm_id || "");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const poll = async () => {
    try {
      const { data } = await api.get(`/scouting-market/players/${player.id}/rffm-status`);
      setJob(data);
      if (RUNNING.includes(data.status)) {
        wasRunning.current = true;
        timer.current = setTimeout(poll, 4000);
      } else if (wasRunning.current) {
        wasRunning.current = false;
        if (data.status === "COMPLETADO") { toast.success("Informe RFFM completado"); onUpdated?.(); }
        if (data.status === "ERROR") toast.error(`Informe RFFM: ${data.error || "error de lectura"}`);
      }
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    poll();
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.id]);

  useEffect(() => { setCode(player.rffm_id || ""); }, [player.rffm_id]);

  const running = job && RUNNING.includes(job.status);
  const hasCode = Boolean(player.rffm_id);

  const start = async () => {
    if (!code.trim()) { toast.error("Introduce el código RFFM del jugador"); return; }
    setBusy(true);
    try {
      if (!hasCode || code.trim() !== String(player.rffm_id)) {
        await api.post(`/scouting-market/players/${player.id}/rffm-code`, { code: code.trim() });
      } else {
        await api.post(`/scouting-market/players/${player.id}/rffm-sync`);
      }
      toast.success("Informe RFFM en cola");
      wasRunning.current = true;
      clearTimeout(timer.current);
      poll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-purple-200 shadow-sm" data-testid="scouting-rffm-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-purple-800 font-bold">
            <Satellite className="h-4 w-4" /> Motor RFFM
          </div>
          {player.rffm_last_sync && (
            <div className="text-[11px] text-slate-500" data-testid="rffm-last-sync">
              Última actualización: <b>{fmtSync(player.rffm_last_sync)}</b> · se actualiza automáticamente cada 15 días
            </div>
          )}
        </div>

        {isAdmin ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Código RFFM del jugador"
              className="h-9 w-52 text-sm"
              disabled={running}
              data-testid="rffm-code-input"
            />
            <Button
              size="sm"
              onClick={start}
              disabled={busy || running}
              className="text-white font-bold"
              style={{ backgroundColor: "#5B2383" }}
              data-testid="rffm-generate-btn"
            >
              {busy || running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              {hasCode && code.trim() === String(player.rffm_id) ? "Actualizar ahora" : "Generar informe RFFM (3 temporadas)"}
            </Button>
          </div>
        ) : !hasCode ? (
          <div className="text-sm text-slate-400 italic">Sin código RFFM asignado.</div>
        ) : null}

        {running && (
          <div className="flex items-center gap-2 rounded-md border border-purple-100 bg-purple-50/50 p-2.5 text-xs text-purple-800 font-semibold" data-testid="rffm-progress">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            {PROGRESS_LABEL[job.progress] || job.progress}
            {job.progress_detail ? <span className="text-purple-600 font-normal">· {job.progress_detail}</span> : null}
          </div>
        )}
        {job?.status === "ERROR" && !running && (
          <div className="rounded-md border border-rose-100 bg-rose-50 p-2.5 text-xs text-rose-700" data-testid="rffm-error">
            Último intento con error: {job.error || "error de lectura RFFM"}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
